import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchBoardTasks, updateTask, type TaskRow } from '../api/tasks';
import { TAG_PILL_COLORS, ROLE_LABELS as TAG_LABELS } from '../constants/taskTags';
import { formatTaskDate, isTaskOverdue } from '../utils/formatTaskDate';
import { mergeStatusFilterOptions, taskMatchesStatusFilter } from '../utils/taskStatusFilter';
import { TaskStatusPills } from './TaskStatusPills';
import { SortableTh, SortHeaderButton } from './SortableTh';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import '../pages/TasksBoard.css';

const PRIORITY_PILL_COLORS: Record<string, string> = {
  Low: '#73BF4380',
  Medium: '#FFC10780',
  High: '#E48B5280',
  Urgent: '#ef444480',
};

const FILTER_COLS = ['status', 'priority', 'tags', 'assigned_to', 'assigned_by'] as const;
type FilterCol = (typeof FILTER_COLS)[number];

const FILTER_LABELS: Record<FilterCol, string> = {
  status: 'Status',
  priority: 'Priority',
  tags: 'Roles',
  assigned_to: 'Assigned to',
  assigned_by: 'Assigned by',
};

interface ProjectListTabProps {
  projectId: number;
  eventId?: number;
  viewerUserId: number;
  /** When true, show actions to unlink tasks from this project (same permission as editing the board). */
  canRemoveFromProject?: boolean;
}

const ProjectListTab: React.FC<ProjectListTabProps> = ({
  projectId,
  eventId,
  viewerUserId,
  canRemoveFromProject = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [removingTaskId, setRemovingTaskId] = useState<number | null>(null);

  const [filters, setFilters] = useState<Record<FilterCol, string[]>>({
    status: [],
    priority: [],
    tags: [],
    assigned_to: [],
    assigned_by: [],
  });
  const [openFilterCol, setOpenFilterCol] = useState<FilterCol | null>(null);
  const [filterDropdownPos, setFilterDropdownPos] = useState<{ top: number; left: number } | null>(null);

  const toggleFilterValue = useCallback((col: FilterCol, value: string) => {
    setFilters((prev) => {
      const cur = prev[col];
      return { ...prev, [col]: cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value] };
    });
  }, []);

  const handleFilterBtnClick = useCallback((col: FilterCol, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (openFilterCol === col) {
      setOpenFilterCol(null);
      setFilterDropdownPos(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setFilterDropdownPos({ top: rect.bottom + 6, left: rect.left });
    setOpenFilterCol(col);
  }, [openFilterCol]);

  useEffect(() => {
    if (!openFilterCol) return;
    const close = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.all-tasks-filter-btn') && !target.closest('.all-tasks-filter-dropdown')) {
        setOpenFilterCol(null);
        setFilterDropdownPos(null);
      }
    };
    const closeOnScroll = (e: Event) => {
      if ((e.target as Element)?.closest?.('.all-tasks-filter-dropdown')) return;
      setOpenFilterCol(null);
      setFilterDropdownPos(null);
    };
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', closeOnScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', closeOnScroll, true);
    };
  }, [openFilterCol]);

  useEffect(() => {
    if (!Number.isInteger(projectId) || projectId < 1 || !Number.isInteger(viewerUserId) || viewerUserId < 1) {
      setTasks([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    fetchBoardTasks(viewerUserId, { projectId, eventId })
      .then((list) => {
        if (!cancelled) setTasks(list);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Failed to load tasks.');
          setTasks([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, eventId, viewerUserId]);

  const uniqueValues = useMemo(() => {
    return {
      status: mergeStatusFilterOptions(tasks.map((t) => t.status)),
      priority: Array.from(new Set(tasks.map((t) => t.priority ?? '—'))).filter(Boolean).sort(),
      tags: Array.from(new Set(tasks.flatMap((t) => t.tags ?? []))).sort(),
      assigned_to: Array.from(
        new Set(
          tasks.flatMap((t) => {
            const parts = t.assignees?.length
              ? t.assignees.map((a) => a.name.trim()).filter(Boolean)
              : t.assigned_to_name
                ? t.assigned_to_name.split(',').map((s) => s.trim()).filter(Boolean)
                : [];
            if (parts.length > 0) return parts;
            return ['—'];
          })
        )
      ).sort(),
      assigned_by: Array.from(new Set(tasks.map((t) => t.assigner_name ?? '—'))).sort(),
    };
  }, [tasks]);

  const taskSort = useTableSortState();
  const filteredTasks = useMemo(() => {
    const { status, priority, tags, assigned_to, assigned_by } = filters;
    return tasks.filter((task) => {
      if (status.length > 0 && !taskMatchesStatusFilter(task, status)) return false;
      if (priority.length > 0 && !priority.includes(task.priority ?? '—')) return false;
      if (tags.length > 0 && !tags.some((tg) => (task.tags ?? []).includes(tg))) return false;
      if (assigned_to.length > 0) {
        const nameParts = task.assignees?.length
          ? task.assignees.map((a) => a.name.trim()).filter(Boolean)
          : task.assigned_to_name
            ? task.assigned_to_name.split(',').map((s) => s.trim()).filter(Boolean)
            : [];
        const matches =
          nameParts.some((n) => assigned_to.includes(n)) ||
          (nameParts.length === 0 && assigned_to.includes('—'));
        if (!matches) return false;
      }
      if (assigned_by.length > 0 && !assigned_by.includes(task.assigner_name ?? '—')) return false;
      return true;
    });
  }, [tasks, filters]);

  const removeTaskFromProject = useCallback(
    async (task: TaskRow) => {
      if (!canRemoveFromProject) return;
      const ok = window.confirm(
        `Remove "${task.name}" from this project?\n\nThe task will remain in your task list but will no longer appear on this project.`
      );
      if (!ok) return;
      setRemovingTaskId(task.id);
      try {
        await updateTask(task.id, { project_id: null, acting_user_id: viewerUserId });
        setTasks((prev) => prev.filter((t) => t.id !== task.id));
      } catch {
        window.alert('Could not remove this task from the project. You may not have permission.');
        try {
          const list = await fetchBoardTasks(viewerUserId, { projectId, eventId });
          setTasks(list);
        } catch {
          /* ignore */
        }
      } finally {
        setRemovingTaskId(null);
      }
    },
    [canRemoveFromProject, viewerUserId, projectId]
  );

  const sortedFilteredTasks = useMemo(
    () =>
      sortRows(filteredTasks, taskSort.sortKey, taskSort.sortDir, (task, key) => {
        switch (key) {
          case 'name':
            return task.name ?? '';
          case 'status':
            return `${task.status ?? ''}\t${isTaskOverdue(task) ? '0' : '1'}`;
          case 'priority':
            return task.priority ?? '';
          case 'tags':
            return (task.tags ?? []).join(', ');
          case 'assigned_to':
            return task.assigned_to_name ?? '';
          case 'assigned_by':
            return task.assigner_name ?? '';
          case 'due_date':
            return task.due_date ?? '';
          default:
            return '';
        }
      }),
    [filteredTasks, taskSort.sortKey, taskSort.sortDir]
  );

  if (!Number.isInteger(viewerUserId) || viewerUserId < 1) {
    return <p className="project-detail-panel-placeholder">Sign in to view project tasks.</p>;
  }

  return (
    <div className="project-list-tab">
      <section className="project-list-section">
        <h3 className="project-list-section-title">Tasks</h3>
        {loading && <p className="project-detail-panel-placeholder">Loading tasks…</p>}
        {loadError && (
          <p className="create-order-error" role="alert">
            {loadError}
          </p>
        )}
        {!loading && !loadError && (
          <>
            <div className="collapsible-table-wrapper">
              <table className="all-tasks-table">
                <thead>
                  <tr>
                    <SortableTh label="Task" columnKey="name" sortKey={taskSort.sortKey} sortDir={taskSort.sortDir} onSort={taskSort.toggleSort} />
                    {FILTER_COLS.map((col) => {
                      const isActive = filters[col].length > 0;
                      return (
                        <th key={col}>
                          <div className="all-tasks-th-content">
                            <SortHeaderButton
                              label={FILTER_LABELS[col]}
                              columnKey={col}
                              sortKey={taskSort.sortKey}
                              sortDir={taskSort.sortDir}
                              onSort={taskSort.toggleSort}
                            />
                            <button
                              type="button"
                              className={`all-tasks-filter-btn${isActive ? ' active' : ''}`}
                              onClick={(e) => handleFilterBtnClick(col, e)}
                              title={`Filter by ${FILTER_LABELS[col]}`}
                            >
                              ▾
                            </button>
                          </div>
                        </th>
                      );
                    })}
                    <SortableTh label="Due date" columnKey="due_date" sortKey={taskSort.sortKey} sortDir={taskSort.sortDir} onSort={taskSort.toggleSort} />
                    {canRemoveFromProject ? (
                      <th scope="col" className="project-list-actions-th">
                        Actions
                      </th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {sortedFilteredTasks.length === 0 ? (
                    <tr>
                      <td colSpan={canRemoveFromProject ? 8 : 7} data-label="">
                        {tasks.length === 0
                          ? 'No tasks on this project yet.'
                          : 'No tasks match the current filters.'}
                      </td>
                    </tr>
                  ) : (
                    sortedFilteredTasks.map((task) => (
                      <tr
                        key={task.id}
                        className="all-tasks-row"
                        onClick={() =>
                          navigate(`/tasks/${task.id}`, { state: { from: location.pathname + location.search } })
                        }
                      >
                        <td data-label="Task">{task.name}</td>
                        <td data-label="Status">
                          <TaskStatusPills task={task} />
                        </td>
                        <td data-label="Priority">
                          {task.priority ? (
                            <span
                              className="dashboard-pill"
                              style={{ backgroundColor: PRIORITY_PILL_COLORS[task.priority] ?? '#e5e7eb' }}
                            >
                              {task.priority}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td data-label="Roles">
                          <div className="dashboard-admin-tasks-tags">
                            {(task.tags?.length ? task.tags : []).map((tag) => (
                              <span
                                key={tag}
                                className="dashboard-pill"
                                style={{ backgroundColor: TAG_PILL_COLORS[tag] ?? '#e5e7eb' }}
                              >
                                {TAG_LABELS[tag] ?? tag.replace(/_/g, ' ')}
                              </span>
                            ))}
                            {(!task.tags || task.tags.length === 0) && '—'}
                          </div>
                        </td>
                        <td data-label="Assigned to">{task.assigned_to_name ?? '—'}</td>
                        <td data-label="Assigned by">{task.assigner_name ?? '—'}</td>
                        <td data-label="Due date">{formatTaskDate(task.due_date)}</td>
                        {canRemoveFromProject ? (
                          <td data-label="Actions" className="project-list-actions-td">
                            <button
                              type="button"
                              className="project-list-remove-from-project-btn"
                              title="Remove from project"
                              disabled={removingTaskId === task.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                void removeTaskFromProject(task);
                              }}
                            >
                              Remove
                            </button>
                          </td>
                        ) : null}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {openFilterCol && filterDropdownPos && (() => {
              const col = openFilterCol;
              const vals = uniqueValues[col];
              const active = filters[col];
              return (
                <div
                  className="all-tasks-filter-dropdown"
                  style={{ top: filterDropdownPos.top, left: filterDropdownPos.left }}
                >
                  {vals.length === 0 && <span className="all-tasks-filter-empty">No options</span>}
                  {vals.map((val) => (
                    <label key={val} className="all-tasks-filter-option">
                      <input
                        type="checkbox"
                        checked={active.includes(val)}
                        onChange={() => toggleFilterValue(col, val)}
                      />
                      {col === 'tags' ? (TAG_LABELS[val] ?? val.replace(/_/g, ' ')) : val}
                    </label>
                  ))}
                  {active.length > 0 && (
                    <button
                      type="button"
                      className="all-tasks-filter-clear"
                      onClick={() => setFilters((p) => ({ ...p, [col]: [] }))}
                    >
                      Clear filter
                    </button>
                  )}
                </div>
              );
            })()}
          </>
        )}
      </section>
    </div>
  );
};

export default ProjectListTab;
